import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, CircularProgress, Snackbar, Alert, Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Category as CategoryIcon, Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const InventoryCategories: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', description: '', color: '#8b5cf6' });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/inventory-module/categories'));
      setCategories(res.data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openDialog = (cat?: any) => {
    if (cat) { setEditingId(cat.id); setForm({ name: cat.name || '', description: cat.description || '', color: cat.color || '#8b5cf6' }); }
    else { setEditingId(null); setForm({ name: '', description: '', color: '#8b5cf6' }); }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSnackbar({ open: true, message: 'Nome obrigatório', severity: 'error' }); return; }
    try {
      setSaving(true);
      if (editingId) { await axios.put(getApiUrl(`/inventory-module/categories/${editingId}`), form); }
      else { await axios.post(getApiUrl('/inventory-module/categories'), form); }
      setSnackbar({ open: true, message: editingId ? 'Atualizada!' : 'Criada!', severity: 'success' });
      setDialogOpen(false); fetch();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Desativar esta categoria?')) return;
    try { await axios.delete(getApiUrl(`/inventory-module/categories/${id}`)); setSnackbar({ open: true, message: 'Desativada!', severity: 'success' }); fetch(); }
    catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Categorias de Produto</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar"><IconButton onClick={fetch}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            Nova Categoria
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cor</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: '#9ca3af' }}>Nenhuma categoria cadastrada</TableCell></TableRow>
              ) : categories.map(cat => (
                <TableRow key={cat.id} hover>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{cat.name}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{cat.description || '-'}</Typography></TableCell>
                  <TableCell><Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: cat.color || '#8b5cf6', border: '2px solid #e5e7eb' }} /></TableCell>
                  <TableCell align="center">
                    <Chip label={cat.is_active ? 'Ativa' : 'Inativa'} size="small" sx={{ bgcolor: cat.is_active ? '#d1fae5' : '#f3f4f6', color: cat.is_active ? '#059669' : '#6b7280', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openDialog(cat)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                    <Tooltip title="Desativar"><IconButton size="small" onClick={() => handleDelete(cat.id)}><DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          {editingId ? 'Editar Categoria' : 'Nova Categoria'}
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth required label="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} size="small" />
            <TextField fullWidth label="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} size="small" multiline rows={2} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">Cor:</Typography>
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }} />
              <Typography variant="caption" color="text.secondary">{form.color}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryCategories;
